import type { PlatoUserMe, SuperAdminMe } from '@/server/types/infer-trpc-types';
import { setLoginAsUserIdForSuperAdmin } from '@/utils/globals';
import { trpcClient } from '@/utils/trpc-client';
import { assign, createMachine, fromPromise } from 'xstate';
import { LOGGED_IN_AS_COOKIE } from '../constants';
import { UserType } from '../types';

export interface MyMachineContext {
    appIsLoading: boolean;
    userIsLoading: boolean;
    primaryUserType: UserType | null;
    platoUser: PlatoUserMe | null;
    tenantId: string | null;
    superAdmin: SuperAdminMe | null;
    superAdminImpersonatingPlatoUserId: string | null;
}

export type AppEvent =
    | { type: 'LOGOUT' }
    | { type: 'DID_LOGOUT_OF_CLERK' }
    | { type: 'USER_LOGGED_IN' }
    | { type: 'START_IMPERSONATION_SESSION'; superAdminImpersonatingPlatoUserId: string }
    | { type: 'END_IMPERSONATING_SESSION' };

const appStateMachine = () =>
    createMachine(
        {
            id: 'auth',
            initial: 'NOT_AUTHENTICATED',
            types: {} as {
                context: MyMachineContext;
                events: AppEvent;
            },
            context: {
                appIsLoading: true,
                userIsLoading: true,
                primaryUserType: null,
                platoUser: null,
                tenantId: null,
                superAdmin: null,
                superAdminImpersonatingPlatoUserId: null,
            },
            on: {
                LOGOUT: {
                    target: '.LOGGING_OUT_OF_CLERK',
                },
                DID_LOGOUT_OF_CLERK: {
                    target: '.NOT_AUTHENTICATED',
                    actions: assign({
                        appIsLoading: true,
                        userIsLoading: true,
                        primaryUserType: null,
                        platoUser: null,
                        tenantId: null,
                        superAdmin: null,
                        superAdminImpersonatingPlatoUserId: null,
                    }),
                },
            },
            states: {
                LOGGING_OUT_OF_CLERK: {
                    invoke: {
                        id: 'logoutOfClerk',
                        src: 'logoutOfClerk',
                    },
                },
                NOT_AUTHENTICATED: {
                    on: {
                        USER_LOGGED_IN: {
                            target: 'LOADED_CLERK',
                            actions: assign({
                                appIsLoading: false,
                            }),
                        },
                    },
                },
                LOADED_CLERK: {
                    invoke: {
                        id: 'getUserType',
                        src: 'getUserType',
                        onDone: [
                            {
                                target: 'LOADING_PLATO_USER',
                                guard: ({ event }) => event.output === UserType.PlatoUser,
                                actions: assign({
                                    primaryUserType: ({ event }) => event.output,
                                }),
                            },
                            {
                                target: 'LOADING_SUPER_ADMIN',
                                guard: ({ event }) => event.output === UserType.SuperAdmin,
                                actions: assign({
                                    primaryUserType: ({ event }) => event.output,
                                }),
                            },
                            {
                                target: 'NO_ACCOUNT',
                                actions: assign({
                                    primaryUserType: null,
                                }),
                            },
                        ],
                    },
                },
                LOADING_PLATO_USER: {
                    invoke: {
                        id: 'getPlatoUser',
                        src: 'getPlatoUser',
                        onDone: {
                            target: 'PLATO_USER',
                            actions: assign({
                                platoUser: ({ event }) => event.output,
                                tenantId: ({ event }) => event.output.platoCustomer.id,
                            }),
                        },
                    },
                },
                LOADING_SUPER_ADMIN: {
                    invoke: {
                        id: 'getSuperAdmin',
                        src: 'getSuperAdmin',
                        onDone: {
                            target: 'CHECK_SUPER_ADMIN_WAS_PREVIOUSLY_IMPERSONATING',
                            actions: assign({
                                superAdmin: ({ event }) => event.output,
                            }),
                        },
                    },
                },
                CHECK_SUPER_ADMIN_WAS_PREVIOUSLY_IMPERSONATING: {
                    invoke: {
                        id: 'checkSuperAdminWasPreviouslyImpersonating',
                        src: 'checkSuperAdminWasPreviouslyImpersonating',
                        onDone: [
                            {
                                target: 'LOADING_IMPOERSONATION_SESSION',
                                guard: ({ event }) =>
                                    event.output !== null &&
                                    event.output !== 'null' &&
                                    event.output !== 'undefined',
                                actions: assign({
                                    superAdminImpersonatingPlatoUserId: ({ event }) => event.output,
                                }),
                            },
                            {
                                target: 'SUPER_ADMIN',
                                guard: ({ event }) =>
                                    event.output === null ||
                                    event.output === 'null' ||
                                    event.output === 'undefined',
                            },
                        ],
                    },
                },
                PLATO_USER: {},
                SUPER_ADMIN: {
                    on: {
                        START_IMPERSONATION_SESSION: {
                            target: 'LOADING_IMPOERSONATION_SESSION',
                            actions: assign({
                                superAdminImpersonatingPlatoUserId: ({ event }) =>
                                    event.superAdminImpersonatingPlatoUserId,
                            }),
                        },
                    },
                },
                LOADING_IMPOERSONATION_SESSION: {
                    invoke: {
                        id: 'fetchImpersonationData',
                        src: 'fetchImpersonationData',
                        input: ({ context }) => ({ context }),
                        onDone: {
                            target: 'IMPOERSONATION_SESSION',
                            actions: assign({
                                platoUser: ({ event }) => event.output,
                                tenantId: ({ event }) => event.output.platoCustomer.id,
                            }),
                        },
                        onError: {
                            target: 'SUPER_ADMIN',
                            actions: [
                                assign({
                                    superAdminImpersonatingPlatoUserId: null,
                                }),
                                () => setLoginAsUserIdForSuperAdmin(null),
                            ],
                        },
                    },
                },
                IMPOERSONATION_SESSION: {
                    on: {
                        END_IMPERSONATING_SESSION: {
                            target: 'ENDING_IMPERSONATION_SESSION',
                        },
                    },
                },
                ENDING_IMPERSONATION_SESSION: {
                    invoke: {
                        id: 'endImpersonationSession',
                        src: 'endImpersonationSession',
                        onDone: {
                            target: 'SUPER_ADMIN',
                            actions: [
                                assign({
                                    superAdminImpersonatingPlatoUserId: null,
                                    platoUser: null,
                                }),
                                () => setLoginAsUserIdForSuperAdmin(null),
                            ],
                        },
                    },
                },
                NO_ACCOUNT: {},
            },
        },
        {
            actors: {
                logoutOfClerk: fromPromise(async () => {
                    // actual logic is implemented by provider component
                }),
                getUserType: fromPromise(async () => {
                    return trpcClient.dangerousGenericUser.userTypeCheck.check.query();
                }),
                getPlatoUser: fromPromise(async () => {
                    return trpcClient.platoUser.me.getMe.query();
                }),
                getSuperAdmin: fromPromise(async () => {
                    return trpcClient.superAdmin.me.getMe.query();
                }),
                checkSuperAdminWasPreviouslyImpersonating: fromPromise(async () => {
                    const retrievedObject = localStorage.getItem(LOGGED_IN_AS_COOKIE) as string;

                    if (retrievedObject && retrievedObject !== 'null' && retrievedObject !== 'undefined') {
                        return retrievedObject;
                    } else {
                        return null;
                    }
                }),
                fetchImpersonationData: fromPromise(
                    async ({ input }: { input: { context: MyMachineContext } }) => {
                        if (input.context.superAdminImpersonatingPlatoUserId) {
                            localStorage.setItem(
                                LOGGED_IN_AS_COOKIE,
                                input.context.superAdminImpersonatingPlatoUserId,
                            );
                        }

                        setLoginAsUserIdForSuperAdmin(
                            input.context.superAdminImpersonatingPlatoUserId || null,
                        );

                        return trpcClient.platoUser.me.getMe.query();
                    },
                ),
                endImpersonationSession: fromPromise(async () => {
                    localStorage.removeItem(LOGGED_IN_AS_COOKIE);
                    return true;
                }),
            },
        },
    );

export { appStateMachine };
